import React, { FunctionComponent, useState } from "react";

/* Validation */
import { Formik, useFormikContext } from "formik";
import validationSchema, { FormData } from "./formConfig";

/* API */
import { STATUS_MESSAGE } from "./statusMessages";
import {
  CallMeResult,
  getHttpStatusAndJson,
  QueueId,
  sendCallMe,
} from "./callMe/sendCallMe";
import {
  Alert,
  Button,
  Flex,
  InputGroup,
  Legend,
  Text,
  Title,
  ValidationMessage,
} from "@gjensidige/builders-components";
import FormInput from "./components/FormInput";

const CallMeForsikringstjek = () => {
  const [statusMessage, setStatusMessage] = useState("");
  const [statusCode, setStatusCode] = useState("");
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const handleFormData = (formData: FormData) => {
    setIsLoading(true);
    setStatusCode("");
    sendCallMe(
      {
        customerName: formData.customerName,
        phoneNumber: formData.phoneNumber,
      },
      formData.queueId!,
    )
      .then((result) =>
        getHttpStatusAndJson(result).then((parsed) => setResults(parsed)),
      )
      .catch(() => {
        setStatusCode("-1");
        setStatusMessage(STATUS_MESSAGE.ERROR);
      });
  };

  const setResults = (callMeResult: CallMeResult) => {
    setStatusCode(callMeResult.data.code);
    setIsLoading(false);
    switch (callMeResult.data.code) {
      case "0":
        setStatusMessage(STATUS_MESSAGE.OK);
        break;
      case "3001":
        setStatusMessage(STATUS_MESSAGE.INVALID_PHONE);
        break;
      case "2010":
        setStatusMessage(STATUS_MESSAGE.ALREADY_REGISTERED);
        break;
      case "4005":
        setStatusMessage(STATUS_MESSAGE.CLOSED);
        break;
      case "-1":
      case "1200":
      case "15":
        setStatusMessage(STATUS_MESSAGE.ERROR);
        break;
    }
  };

  return (
    <Flex center={"md"} fill layout={1}>
      {statusCode === "0" ? (
        <Alert severity="success" tracking={{eventLabel: 'tak-for-din-henvendelse'}} style={{ whiteSpace: "pre-line" }}>
          <Title>Tak for din henvendelse</Title>
          <Text>
            {`Vi kontakter dig hurtigst muligt i tidsrummet man-tor: 8:00-18:00 og fre: 8:30-16:00.`}
          </Text>
        </Alert>
      ) : (
        <Formik
          enableReinitialize
          initialValues={{
            customerName: "",
            phoneNumber: "",
            queueId: undefined,
          }}
          validationSchema={validationSchema()}
          onSubmit={(values: FormData) => {
            handleFormData(values);
          }}
        >
          <RenderForm isLoading={isLoading} />
        </Formik>
      )}
      {statusCode !== "0" && statusCode !== "" && (
        <Alert severity="error" style={{ whiteSpace: "pre-line" }} tracking={{eventLabel: 'der-skete-en-fejl'}}>
          <Title>Der skete en fejl.</Title>
          <Text>{statusMessage}</Text>
        </Alert>
      )}
    </Flex>
  );
};

interface RenderProps {
  isLoading: boolean;
}

const RenderForm: FunctionComponent<RenderProps> = ({ isLoading }) => {
  const { handleSubmit, handleBlur, setFieldValue, touched, errors, values } =
    useFormikContext<FormData>();

  return (
    <form onSubmit={handleSubmit}>
      <Flex layout={1}>
        <FormInput
          id="customerName"
          label="Navn *"
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            setFieldValue("customerName", event.target.value);
          }}
          onBlur={handleBlur}
          invalid={!!(touched.customerName && errors.customerName)}
          value={values.customerName}
          errorText={errors.customerName}
        />

        <FormInput
          id="phoneNumber"
          label="Telefonnummer *"
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            setFieldValue("phoneNumber", event.target.value);
          }}
          onBlur={handleBlur}
          invalid={!!(touched.phoneNumber && errors.phoneNumber)}
          value={values.phoneNumber}
          errorText={errors.phoneNumber}
        />

        <InputGroup
          variant={"column"}
          id="queueId"
          name="queueId"
          onChange={(value) => {
            setFieldValue("queueId", value.at(0));
          }}
          type="radio"
          value={values.queueId}
          onBlur={handleBlur}
          invalid={!!(touched.queueId && errors.queueId)}
        >
          <Legend>Er du kunde hos os i dag? *</Legend>
          <InputGroup.Input label="Ja" value={QueueId.PrivatForsikring} />
          <InputGroup.Input label="Nej" value={QueueId.Sales} />

          <ValidationMessage
            htmlFor={"queueId"}
            hidden={!(touched.queueId && errors.queueId)}
            invalid={!!(touched.queueId && errors.queueId)}
          >
            {errors.queueId}
          </ValidationMessage>
        </InputGroup>
        <Button loading={isLoading} type="submit" flexible>
          Ring mig op
        </Button>
      </Flex>
    </form>
  );
};

export default CallMeForsikringstjek;
